import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faFilter, faSortAmountDown, faEye,
  faTrash, faCheck, faTimes, faPlus, faCog 
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import adminService from '../../services/adminService';
import '../admin/AdminStyles.css';

function ProfessionalListingsList() {
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    is_active: '',
    is_paid: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [professionalToDelete, setProfessionalToDelete] = useState(null);
  const [listingPrice, setListingPrice] = useState(0);
  
  useEffect(() => {
    fetchProfessionalListings();
    fetchListingPrice();
  }, [currentPage, filter]);
  
  const fetchProfessionalListings = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        is_active: filter.is_active,
        is_paid: filter.is_paid,
        name: searchTerm
      };
      
      const response = await adminService.getProfessionalListings(params);
      setProfessionals(response.professionals);
      setTotalPages(response.pages);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching professional listings:', error);
      setError('Failed to load professional listings');
      setIsLoading(false);
    }
  };
  
  const fetchListingPrice = async () => {
    try {
      const data = await adminService.getProfessionalListingPrice();
      setListingPrice(data.member_price);
    } catch (error) {
      console.error('Error fetching listing price:', error);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchProfessionalListings();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const handleDelete = (professional) => {
    setProfessionalToDelete(professional);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      await adminService.deleteProfessionalListing(professionalToDelete.id);
      setProfessionals(professionals.filter(p => p.id !== professionalToDelete.id));
      setShowDeleteModal(false);
      setProfessionalToDelete(null);
    } catch (error) {
      console.error('Error deleting professional listing:', error);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProfessionalToDelete(null);
  };

  const toggleProfessionalStatus = async (professional) => {
    try {
      const response = await adminService.toggleProfessionalListingStatus(professional.id);
      
      const updatedProfessionals = professionals.map(p => {
        if (p.id === professional.id) {
          return {
            ...p,
            is_active: response.is_active
          };
        }
        return p;
      });
      
      setProfessionals(updatedProfessionals);
    } catch (error) {
      console.error('Error toggling professional status:', error);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Professional Listings</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">Professional Listings</li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search professionals..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="is_active"
                value={filter.is_active}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              
              <select
                name="is_paid"
                value={filter.is_paid}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Payments</option>
                <option value="true">Paid</option>
                <option value="false">Unpaid</option>
              </select>
              
              <Link to="/admin/professional-listing-pricing" className="admin-btn admin-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
                <FontAwesomeIcon icon={faCog} />
                Set Pricing ({formatCurrency(listingPrice)})
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>{error}</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Expertise</th>
                <th>Email</th>
                <th>Payment Status</th>
                <th>Status</th>
                <th>Valid Until</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {professionals.length > 0 ? (
                professionals.map((professional) => (
                  <tr key={professional.id}>
                    <td>{professional.id}</td>
                    <td>
                      <div style={{ fontWeight: '600' }}>{professional.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{professional.qualification}</div>
                    </td>
                    <td>{professional.expertise}</td>
                    <td>{professional.email}</td>
                    <td>
                      <span 
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          backgroundColor: professional.is_paid ? '#d4edda' : '#f8d7da',
                          color: professional.is_paid ? '#155724' : '#721c24'
                        }}
                      >
                        {professional.is_paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td>
                      <span 
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          backgroundColor: professional.is_active ? '#d4edda' : '#f8d7da',
                          color: professional.is_active ? '#155724' : '#721c24'
                        }}
                      >
                        {professional.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(professional.valid_until)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <Link 
                          to={`/admin/professional-listings/${professional.id}`}
                          className="admin-table-action-btn"
                          title="View"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <button
                          className="admin-table-action-btn"
                          onClick={() => toggleProfessionalStatus(professional)}
                          title={professional.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <FontAwesomeIcon icon={professional.is_active ? faTimes : faCheck} />
                        </button>
                        <button
                          className="admin-table-action-btn"
                          onClick={() => handleDelete(professional)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                    No professional listings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {professionals.length > 0 && totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '1.5rem' 
          }}>
            <div className="admin-pagination">
              <button 
                className="admin-pagination-btn"
                style={{ 
                  border: '1px solid var(--admin-border)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                &laquo;
              </button>
              <button 
                className="admin-pagination-btn"
                style={{ 
                  border: '1px solid var(--admin-border)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &lsaquo;
              </button>
              
              <span style={{ 
                padding: '0.5rem 0.75rem',
                borderTop: '1px solid var(--admin-border)',
                borderBottom: '1px solid var(--admin-border)',
              }}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                className="admin-pagination-btn"
                style={{ 
                  border: '1px solid var(--admin-border)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &rsaquo;
              </button>
              <button 
                className="admin-pagination-btn"
                style={{ 
                  border: '1px solid var(--admin-border)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h3 style={{ marginTop: 0 }}>Delete Professional Listing</h3>
            <p>
              Are you sure you want to delete the listing for <strong>{professionalToDelete?.name}</strong>? 
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
              <button 
                className="admin-btn" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ProfessionalListingsList;
